import { useMemo, useState } from 'react';
import { AlertTriangle, FolderOpen, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  LwButton,
  LwEmpty,
  LwField,
  LwModal,
  LwSegmented,
  LwTable,
  LwThemeScope
} from '@/components/lw';
import { FraudAlertStatusPill } from '@/components/fraud-alerts/FraudAlertStatusPill';
import {
  reviewFraudAlert,
  type FraudAlert,
  type FraudAlertDisposition
} from '@/services/fraudAlertService';

interface FraudAlertsTabProps {
  alerts: FraudAlert[];
  loading: boolean;
  loadError: string | null;
  canReview: boolean;
  onRefresh: () => Promise<void>;
  onOpenLoanDocuments?: () => void;
}

const DISPOSITION_ITEMS = [
  { value: 'false_alarm', label: 'Reviewed — False Alarm' },
  { value: 'amended_return_accurate', label: 'Reviewed — Amended Return Accurate' }
] as const;

const ATTACHMENT_REASON_LABELS: Record<string, string> = {
  attachment_not_requested: 'Original alert did not request an evidence PDF',
  source_identity_missing: 'Source identity unavailable',
  page_count_unavailable: 'Page count unavailable',
  page_markers_mismatch: 'Page map mismatch',
  evidence_page_unavailable: 'Evidence page unavailable',
  full_document_evidence: 'Safe page-only selection unavailable',
  storage_path_missing: 'Source file unavailable',
  directive_rejected: 'Attachment request rejected',
  event_unverified: 'Detection event could not be verified',
  source_too_large: 'Source file too large',
  download_failed: 'Download failed',
  source_bytes_changed: 'Source file changed after analysis',
  page_count_mismatch: 'PDF page count changed',
  sanitize_failed: 'Evidence PDF could not be rebuilt',
  output_too_large: 'Evidence PDF too large',
  attachment_build_failed: 'Evidence PDF preparation failed',
  provider_rejected: 'Email provider rejected the attachment'
};

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}

function attachmentLabel(alert: FraudAlert): string {
  if (alert.attachmentState === 'included') {
    return 'Evidence PDF included';
  }
  if (alert.attachmentState === 'pending') {
    return 'Pending email outcome';
  }
  if (alert.attachmentState === 'omitted') {
    return ATTACHMENT_REASON_LABELS[alert.attachmentReason || ''] || 'Evidence PDF not attached';
  }
  return alert.detectorVersion === 'legacy'
    ? 'Historical attachment outcome unavailable'
    : 'Attachment outcome unavailable';
}

function evidencePageLabel(alert: FraudAlert): string {
  if (alert.evidencePages.length === 0) {
    return alert.detectorVersion === 'legacy'
      ? 'Original alert page data unavailable'
      : 'No evidence page recorded';
  }
  const prefix = alert.detectorVersion === 'legacy' ? 'Reported page' : 'Evidence page';
  return `${prefix}${alert.evidencePages.length === 1 ? '' : 's'} ${alert.evidencePages.join(', ')}`;
}

function alertTitle(alert: FraudAlert): string {
  return alert.documentName || alert.indicators[0] || 'Amended return detected';
}

function indicatorDetail(alert: FraudAlert): string {
  const indicators = alert.documentName ? alert.indicators : alert.indicators.slice(1);
  return indicators.join(' · ');
}

function alertMetadata(alert: FraudAlert): string {
  const parts = [`${alert.confidence.toUpperCase()} confidence`];
  if (typeof alert.totalWeight === 'number') {
    parts.push(`score ${alert.totalWeight}`);
  }
  parts.push(alert.sourceType, alert.detectorVersion);
  return parts.join(' · ');
}

export function FraudAlertsTab({
  alerts,
  loading,
  loadError,
  canReview,
  onRefresh,
  onOpenLoanDocuments
}: FraudAlertsTabProps) {
  const [reviewTarget, setReviewTarget] = useState<FraudAlert | null>(null);
  const [disposition, setDisposition] = useState<FraudAlertDisposition>('false_alarm');
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const openCount = useMemo(
    () => alerts.filter(alert => alert.reviewStatus === 'open').length,
    [alerts]
  );

  const openReview = (alert: FraudAlert) => {
    setReviewTarget(alert);
    setDisposition('false_alarm');
    setExplanation('');
    setSubmitError(null);
  };

  const closeReview = () => {
    if (!submitting) {
      setReviewTarget(null);
      setSubmitError(null);
    }
  };

  const submitReview = async () => {
    if (!reviewTarget || submitting) {
      return;
    }
    const trimmedExplanation = explanation.trim();
    if (disposition === 'amended_return_accurate' && !trimmedExplanation) {
      setSubmitError('Explain what was amended on the tax return.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await reviewFraudAlert(
        reviewTarget.id,
        disposition,
        disposition === 'amended_return_accurate' ? trimmedExplanation : null
      );
      await onRefresh();
      setReviewTarget(null);
      toast.success('Fraud alert disposition saved.');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to save the review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LwThemeScope>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="eyebrow">Loan review</div>
            <h2 className="font-serif text-2xl font-medium text-primary">Fraud Alerts</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Review page-local amended-return detections and record a terminal disposition.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onOpenLoanDocuments && (
              <LwButton
                variant="sm"
                className="inline-flex items-center gap-2"
                onClick={onOpenLoanDocuments}
              >
                <FolderOpen className="h-4 w-4" aria-hidden="true" />
                Open Loan Documents
              </LwButton>
            )}
            {openCount > 0 && (
              <div className="inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/10 px-3 py-1.5 text-sm font-semibold text-destructive">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                {openCount} open
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Loading fraud alerts…
          </div>
        )}

        {!loading && loadError && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-5" role="alert">
            <p className="font-medium text-destructive">Fraud alerts could not be loaded.</p>
            <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
            <LwButton className="mt-4" variant="sm" onClick={() => void onRefresh()}>
              Try again
            </LwButton>
          </div>
        )}

        {!loading && !loadError && alerts.length === 0 && (
          <LwEmpty title="No fraud alerts">
            This loan has no amended-return alert history.
          </LwEmpty>
        )}

        {!loading && !loadError && alerts.length > 0 && (
          <LwTable
            presentation="canonical"
            density="compact"
            ariaLabel="Fraud alerts for this loan"
          >
            <thead>
              <tr>
                <th scope="col">Detected</th>
                <th scope="col">Alert</th>
                <th scope="col">Evidence</th>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col" className="is-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map(alert => (
                <tr key={alert.id}>
                  <td className="whitespace-nowrap align-top">{formatDateTime(alert.detectedAt)}</td>
                  <td className="min-w-64 align-top">
                    <div className="font-medium text-foreground">
                      {alertTitle(alert)}
                    </div>
                    {alert.documentFolder && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        eFolder: {alert.documentFolder}
                      </div>
                    )}
                    {indicatorDetail(alert) && (
                      <div className="mt-1 text-xs text-foreground">
                        {indicatorDetail(alert)}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                      {alertMetadata(alert)}
                    </div>
                    {alert.reviewNote && (
                      <div className="mt-2 text-sm text-foreground">
                        <span className="font-medium">What was amended:</span> {alert.reviewNote}
                      </div>
                    )}
                  </td>
                  <td className="align-top">
                    <div>{evidencePageLabel(alert)}</div>
                    {alert.detectorVersion === 'legacy' && alert.evidencePages.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Preserved from the original alert
                      </div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">{attachmentLabel(alert)}</div>
                  </td>
                  <td className="whitespace-nowrap align-top">
                    {alert.notified ? formatDateTime(alert.notifiedAt) : 'Not sent'}
                  </td>
                  <td className="align-top">
                    <FraudAlertStatusPill status={alert.reviewStatus} />
                    {alert.reviewedAt && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatDateTime(alert.reviewedAt)}
                      </div>
                    )}
                  </td>
                  <td className="text-right align-top">
                    {alert.reviewStatus === 'open' && canReview ? (
                      <LwButton variant="sm-solid" onClick={() => openReview(alert)}>
                        Review
                      </LwButton>
                    ) : alert.reviewStatus === 'open' ? (
                      <span className="text-xs text-muted-foreground">Read only</span>
                    ) : (
                      <ShieldCheck className="ml-auto h-5 w-5 text-success" aria-label="Reviewed" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </LwTable>
        )}
      </div>

      {reviewTarget && (
        <LwModal
          eyebrow="Open fraud alert"
          title="Record disposition"
          sub={`Detected ${formatDateTime(reviewTarget.detectedAt)}. Reviewed alerts are final.`}
          onClose={closeReview}
          footer={(
            <>
              <LwButton variant="sm" onClick={closeReview} disabled={submitting}>
                Cancel
              </LwButton>
              <LwButton
                variant="sm-solid"
                onClick={() => void submitReview()}
                disabled={submitting || (disposition === 'amended_return_accurate' && !explanation.trim())}
              >
                {submitting ? <><Loader2 className="animate-spin" aria-hidden="true" /> Saving…</> : 'Save disposition'}
              </LwButton>
            </>
          )}
        >
          <div className="space-y-5">
            <LwSegmented
              mode="single-select"
              ariaLabel="Fraud alert disposition"
              value={disposition}
              items={DISPOSITION_ITEMS}
              onValueChange={setDisposition}
            />

            {disposition === 'amended_return_accurate' && (
              <LwField
                presentation="canonical"
                label="What was amended on the tax return?"
                required
                span="full"
                error={submitError}
                hint="Describe the amended item clearly enough for the loan record."
              >
                <textarea
                  rows={5}
                  maxLength={2000}
                  value={explanation}
                  onChange={event => {
                    setExplanation(event.target.value);
                    if (submitError) {
                      setSubmitError(null);
                    }
                  }}
                />
              </LwField>
            )}

            {disposition === 'false_alarm' && submitError && (
              <p className="text-sm text-destructive" role="alert">{submitError}</p>
            )}
          </div>
        </LwModal>
      )}
    </LwThemeScope>
  );
}
